import { useEffect, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router';
import { AppShell, Badge, DropdownMenu, IconButton, Navigation, NavigationGroup, NavigationItem, NavigationProductSwitcher, WorkspaceBadge } from '@humanforce/mango-ui-react';
import { authService } from '../../auth/service';
import { userStore } from '../../auth/store';
import { HOME_PATH, itemPath, navSections } from '../../navigation/navConfig';
import { getNotificationOpenCount } from '../../features/stpNotifications/stpNotificationsApi';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout = observer(({ children }: MainLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationBusinessRecordId, setNotificationBusinessRecordId] = useState(() => localStorage.getItem('stpBusinessRecordId') ?? '');

  useEffect(() => {
    const refresh = () => {
      const businessRecordId = localStorage.getItem('stpBusinessRecordId');
      setNotificationBusinessRecordId(businessRecordId ?? '');
      if (!businessRecordId) { setNotificationCount(0); return; }
      void getNotificationOpenCount(businessRecordId).then(setNotificationCount).catch(() => setNotificationCount(0));
    };
    refresh();
    window.addEventListener('stp-business-record-changed', refresh);
    window.addEventListener('stp-notifications-changed', refresh);
    return () => { window.removeEventListener('stp-business-record-changed', refresh); window.removeEventListener('stp-notifications-changed', refresh); };
  }, [location.pathname]);

  const isHomeActive = path === HOME_PATH;
  const isItemActive = (target: string) => path === target || path.startsWith(`${target}/`);
  const handleSignOut = () => {
    authService.logout({ redirect: false });
    navigate('/login', { replace: true });
  };

  const sidebar = (
    <Navigation tone="red" productSwitcher={<NavigationProductSwitcher icon="brand-payroll">Payroll</NavigationProductSwitcher>}>
      {navSections.map((section) => {
        // Home is the only direct destination; every other section is a non-navigating group.
        if (section.home)
          return (
            <NavigationItem key="home" prefix={section.icon} active={isHomeActive} onClick={() => navigate(HOME_PATH)}>
              {section.label}
            </NavigationItem>
          );
        return (
          <NavigationGroup key={section.slug} label={section.label} icon={section.icon} open={section.items?.some((item) => isItemActive(itemPath(section, item)))}>
            {section.items?.filter((item) => !item.hidden && (!item.requiredRole || userStore.roles.includes(item.requiredRole))).map((item) => (
              <NavigationItem key={item.slug} active={isItemActive(itemPath(section, item))} onClick={() => navigate(itemPath(section, item))}>
                {item.label}
              </NavigationItem>
            ))}
          </NavigationGroup>
        );
      })}
    </Navigation>
  );

  return (
    <AppShell
      fillViewport
      logo="/humanforce-logo-yellow.svg"
      logoShort="/humanforce-monogram-yellow.svg"
      logoAlt="Humanforce"
      topLeft={<WorkspaceBadge label="Company Name" />}
      topRight={
        <>
          <span className="mgo-cluster mgo-gap-xs"><IconButton icon="notifications" label="STP notifications" variant="tertiary" size="small" href={`/maintenance/stp-setup/notifications${notificationBusinessRecordId ? `?businessRecordId=${encodeURIComponent(notificationBusinessRecordId)}` : ''}`} />{notificationCount > 0 && <Badge value={notificationCount > 99 ? '99+' : notificationCount} tone="critical" />}</span>
          <IconButton icon="help" label="Help" variant="tertiary" size="small" />
          <DropdownMenu
            trigger={<IconButton icon="person" label="My account" variant="tertiary" size="small" />}
            items={[
              { label: 'My profile', icon: 'person' },
              { label: 'Settings', icon: 'settings' },
              { separator: true },
              { label: 'Sign out', icon: 'logout', variant: 'destructive', onClick: handleSignOut },
            ]}
          />
        </>
      }
      sidebar={sidebar}
    >
      <main>{children}</main>
    </AppShell>
  );
});
